import { useState, useEffect, useMemo, useRef } from 'react';
import $ from 'jquery';
import 'bootstrap-datepicker';
import 'bootstrap-datepicker/dist/css/bootstrap-datepicker.standalone.css';
import TopNav from '../components/TopNav';
import { ordersApi, walletApi } from '../services/api';
import { useApp } from '../AppContext';

// Fallback checks used before API data loads
const CHECKS_FALLBACK = [
  ['ADDRESS', 0], ['ALCOHOL TEST', 100], ['BANK STATEMENT', 0], ['BANKRUPTCY CHECK', 0],
  ['CREDENTIAL', 0], ['CREDIT CHECK', 650], ['CRIMINAL COURT', 0], ['DATABASE CHECK', 250],
  ['DIRECTORSHIP', 0], ['DRUG TEST', 100], ['EDUCATION', 100], ['EMPLOYMENT', 150],
  ['FINANCIAL REGULATORY', 0], ['FINGERPRINT CHECK', 0], ['GAP CHECK', 100],
  ['IDENTITY (PAN)', 200], ['IDENTITY (AADHAAR)', 200], ['POLICE VERIFICATION', 0],
  ['REFERENCE CHECK', 150], ['SOCIAL MEDIA', 200],
];

const PKGS = {
  standard: { label: 'Standard', price: 600, checks: { 'CRIMINAL COURT': 1, 'IDENTITY (PAN)': 1, 'IDENTITY (AADHAAR)': 1, ADDRESS: 1 } },
  standard_plus: { label: 'Standard Plus', price: 850, checks: { 'CRIMINAL COURT': 1, 'IDENTITY (PAN)': 1, 'IDENTITY (AADHAAR)': 1, ADDRESS: 1, EDUCATION: 1 } },
  premium: { label: 'Premium', price: 1200, checks: { 'CRIMINAL COURT': 1, 'IDENTITY (PAN)': 1, 'IDENTITY (AADHAAR)': 1, ADDRESS: 1, EDUCATION: 1, EMPLOYMENT: 2 } },
  executive: { label: 'Executive', price: 2000, checks: { 'CRIMINAL COURT': 1, 'IDENTITY (PAN)': 1, 'IDENTITY (AADHAAR)': 1, ADDRESS: 1, EDUCATION: 1, EMPLOYMENT: 2, 'SOCIAL MEDIA': 1, 'REFERENCE CHECK': 2 } },
};

let WALLET = 0;

function pkgQtys(pkgKey, checks) {
  if (!pkgKey || !PKGS[pkgKey]) return new Array(checks.length).fill(0);
  const pkgChecks = PKGS[pkgKey].checks;
  return checks.map(([name]) => pkgChecks[name] || 0);
}

// Maps getpackagesdetails response to a qtys array aligned with checks (exact name match only).
function packageDetailsToQtys(details, checks) {
  const qtys = new Array(checks.length).fill(0);
  details.forEach(detail => {
    const qty = parseInt(detail.value, 10) || 0;
    if (!qty) return;
    const textTarget = detail.text?.toUpperCase();
    let idx = textTarget ? checks.findIndex(([name]) => name.toUpperCase() === textTarget) : -1;
    if (idx === -1) {
      const fallback = detail.mainchecktype?.toUpperCase();
      if (fallback) idx = checks.findIndex(([name]) => name.toUpperCase() === fallback);
    }
    if (idx !== -1) qtys[idx] += qty;
  });
  return qtys;
}

function calcTotals(qtys, checks, packagePrice = 0, baseQtys = null) {
  let sub;
  if (packagePrice > 0 && baseQtys) {
    const additional = qtys.reduce((acc, q, i) => {
      const extra = Math.max(0, q - (baseQtys[i] || 0));
      return acc + extra * (checks[i]?.[1] || 0);
    }, 0);
    sub = packagePrice + additional;
  } else {
    sub = qtys.reduce((acc, q, i) => acc + q * (checks[i]?.[1] || 0), 0);
  }
  return { sub, total: sub };
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function formatDisplay(d) {
  if (!d) return '';
  const [yyyy, mm, dd] = d.split('-');
  return `${dd}-${MONTHS[parseInt(mm, 10) - 1]}-${yyyy}`;
}

function toIso(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function DateField({ value, onChange, placeholder = 'dd-MMM-yyyy', id, inputStyle, block }) {
  const ref = useRef(null);

  useEffect(() => {
    const $input = $(ref.current);
    const maxDob = new Date();
    maxDob.setFullYear(maxDob.getFullYear() - 18);
    $input.datepicker({
      format: 'dd-M-yyyy',
      autoclose: true,
      todayHighlight: true,
      clearBtn: false,
      endDate: maxDob,
      orientation: 'bottom auto',
    });
    $input.on('changeDate', e => onChange(toIso(e.date)));
    return () => { $input.datepicker('destroy'); $input.off('changeDate'); };
  }, []);

  return (
    <div style={{ position: 'relative', display: block ? 'flex' : 'inline-flex', alignItems: 'center', width: block ? '100%' : undefined }}>
      <input
        id={id}
        ref={ref}
        className="fsel"
        type="text"
        readOnly
        defaultValue={formatDisplay(value)}
        placeholder={placeholder}
        style={{ cursor: 'pointer', paddingRight: 28, minWidth: block ? undefined : 130, width: block ? '100%' : undefined, ...inputStyle }}
      />
      <span style={{ position: 'absolute', right: 8, cursor: 'pointer', color: 'var(--g400)', display: 'flex', alignItems: 'center', pointerEvents: 'none' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      </span>
    </div>
  );
}

function QtyInput({ value, onAdj }) {
  return (
    <div className="qty-stepper">
      <button className="qty-btn qty-btn-minus" onClick={() => onAdj(-1)}>−</button>
      <span className="qty-val">{value}</span>
      <button className="qty-btn qty-btn-plus" onClick={() => onAdj(1)}>+</button>
    </div>
  );
}

function VerificationScope({ checks, qtys, onQtyChange, onReset, packageList = [], packagesLoaded = true, onEffectiveChecksChange, onPackageChange, onPackagePriceChange, onBaseQtysChange, onPkgLabelChange }) {
  const [mode, setMode] = useState('pkg');
  const [pkgKey, setPkgKey] = useState('');
  const [search, setSearch] = useState('');
  const [localPkgPrice, setLocalPkgPrice] = useState(0);
  const [localBaseQtys, setLocalBaseQtys] = useState(null);
  const [localPkgLabel, setLocalPkgLabel] = useState('');

  // While packages are still loading, assume they may exist so the toggle doesn't flash away
  const hasPackages = !packagesLoaded || packageList.length > 0;

  useEffect(() => {
    if (packagesLoaded && packageList.length === 0 && mode !== 'alc') {
      handleModeChange('alc');
    }
  }, [packagesLoaded, packageList.length]);

  // Prices are shown only when a mode is actively selected; otherwise every check shows 0
  const isActive = mode === 'alc' || (mode === 'pkg' && pkgKey !== '' && pkgKey !== '0');
  const displayChecks = isActive ? checks : checks.map(([name]) => [name, 0]);

  function handleModeChange(m) {
    setMode(m);
    if (m === 'pkg') {
      const effectiveKey = pkgKey === '0' ? '' : pkgKey;
      setPkgKey(effectiveKey);
      onQtyChange(pkgQtys(effectiveKey, checks));
      onEffectiveChecksChange(effectiveKey ? checks : checks.map(([name]) => [name, 0]));
      onPackageChange?.(effectiveKey);
      if (effectiveKey) {
        onPackagePriceChange?.(localPkgPrice);
        onBaseQtysChange?.(localBaseQtys);
        onPkgLabelChange?.(localPkgLabel);
      } else {
        onPackagePriceChange?.(0);
        onBaseQtysChange?.(null);
        onPkgLabelChange?.('');
      }
    } else {
      onReset();
      onEffectiveChecksChange(checks);
      onPackageChange?.('0');
      onPackagePriceChange?.(0);
      onBaseQtysChange?.(null);
      onPkgLabelChange?.('');
    }
  }

  async function handlePkgChange(key) {
    if (key === '0') {
      setMode('alc');
      setPkgKey('0');
      onReset();
      onEffectiveChecksChange(checks);
      onPackageChange?.(key);
      onPackagePriceChange?.(0);
      onBaseQtysChange?.(null);
      onPkgLabelChange?.('');
      return;
    }
    setMode('pkg');
    setPkgKey(key);
    onPackageChange?.(key);
    if (!key) {
      onQtyChange(new Array(checks.length).fill(0));
      onEffectiveChecksChange(checks.map(([name]) => [name, 0]));
      onPackagePriceChange?.(0);
      onBaseQtysChange?.(null);
      onPkgLabelChange?.('');
      setLocalPkgPrice(0);
      setLocalBaseQtys(null);
      setLocalPkgLabel('');
      return;
    }
    const pkg = packageList.find(p => String(p.value) === String(key));
    const price = parseInt(pkg?.price, 10) || 0;
    const label = pkg?.text || '';
    setLocalPkgPrice(price);
    setLocalPkgLabel(label);
    onPackagePriceChange?.(price);
    onPkgLabelChange?.(label);
    onEffectiveChecksChange(checks);
    try {
      const details = await ordersApi.getPackagesDetails(key);
      const newQtys = Array.isArray(details) && details.length > 0
        ? packageDetailsToQtys(details, checks)
        : new Array(checks.length).fill(0);
      onQtyChange(newQtys);
      setLocalBaseQtys(newQtys);
      onBaseQtysChange?.(newQtys);
    } catch {
      const zeros = new Array(checks.length).fill(0);
      onQtyChange(zeros);
      setLocalBaseQtys(zeros);
      onBaseQtysChange?.(zeros);
    }
  }

  function adj(i, delta) {
    const next = [...qtys];
    next[i] = Math.max(0, next[i] + delta);
    onQtyChange(next);
  }

  const q = search.toLowerCase();

  return (
    <>
      {hasPackages && (
        <div className="mode-toggle">
          <div className={`mode-opt${mode === 'pkg' ? ' on' : ''}`} onClick={() => handleModeChange('pkg')}>
            <div className="mode-radio" />
            <div>
              <div className="mode-title">📦 Select Package</div>
              <div className="mode-desc">Pre-defined bundles — quantities editable</div>
            </div>
          </div>
          <div className={`mode-opt${mode === 'alc' ? ' on' : ''}`} onClick={() => handleModeChange('alc')}>
            <div className="mode-radio" />
            <div>
              <div className="mode-title">🛒 A-la-carte</div>
              <div className="mode-desc">Pick individual checks &amp; quantities</div>
            </div>
          </div>
        </div>
      )}

      {hasPackages && mode === 'pkg' && (
        <div className="fg full mb12">
          <label className="flbl">BGV Package <span className="req">*</span></label>
          <select className="sel" value={pkgKey} onChange={e => handlePkgChange(e.target.value)}>
            <option value="">Select Package</option>
            {packageList.map(p => (
              <option key={p.value} value={p.value} price={p.price} currency={p.currency}>
                {p.text}
              </option>
            ))}
          </select>
          <div className="hint">After selecting a package, you can still adjust individual check quantities below.</div>
        </div>
      )}

      <div className="card mt12">
        <div className="chd">
          <div>
            <div className="ctitle">
              {mode === 'pkg' ? '✅ Checks Included — edit quantities if needed' : '🛒 Select Checks'}
            </div>
            <div className="csub">
              {mode === 'pkg' ? 'Package loads checks below · adjust qty with + / −' : 'Pick individual checks & quantities · prices excl. GST'}
            </div>
          </div>
          <div className="flex g8 aic">
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--g400)' }}>🔍</span>
              <input
                className="inp bsm"
                style={{ paddingLeft: 26, width: 160, fontSize: 12 }}
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <button className="btn bghost bsm" onClick={() => { onReset(); setSearch(''); }}>Reset</button>
          </div>
        </div>
        <div className="checks-wrap">
          <table>
            <thead>
              <tr>
                <th>Check Type</th>
                <th>Unit Price</th>
                <th>Qty</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {displayChecks.map(([name, price], i) => {
                if (q && !name.toLowerCase().includes(q)) return null;
                const qty = qtys[i] ?? 0;
                return (
                  <tr key={name} className={qty > 0 ? 'has-qty' : ''}>
                    <td style={{ padding: '9px 13px' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--nv)' }}>{name}</div>
                    </td>
                    <td style={{ padding: '9px 13px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: 'var(--or-dk)', whiteSpace: 'nowrap' }}>
                      {price === 0
                        ? <span style={{ color: 'var(--g300)', fontSize: 11 }}>Std.</span>
                        : `₹${price}`}
                    </td>
                    <td style={{ padding: '9px 13px', textAlign: 'center' }}>
                      <QtyInput value={qty} onAdj={d => adj(i, d)} />
                    </td>
                    <td style={{ padding: '9px 13px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: qty > 0 ? 'var(--or-dk)' : 'var(--g300)' }}>
                      {qty > 0 ? `₹${(qty * price).toLocaleString('en-IN')}` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 10, padding: '9px 12px', background: 'var(--or-lt)', borderRadius: 'var(--rs)', border: '1px solid rgba(245,166,35,.4)', fontSize: 11.5, color: 'var(--g800)' }}>
          <strong>💡 Tip:</strong> For <em>Employment</em>, qty = no. of past employers to verify. For <em>Education</em>, qty = no. of degrees.
        </div>
      </div>
    </>
  );
}

function OrderSummary({ checks, qtys, isInvite, onPlace, submitting, onCancel, onNavigate, packagePrice = 0, baseQtys = null, pkgLabel = '' }) {
  const { total } = calcTotals(qtys, checks, packagePrice, baseQtys);
  const after = WALLET - total;
  const insufficient = after < 0;
  const hasChecks = total > 0;
  return (
    <div style={{ position: 'sticky', top: 80 }}>
      <div className="card mb16">
        <div className="chd"><div className="ctitle">🧾 Order Summary</div></div>

        <div style={{ marginBottom: 14, minHeight: 60 }}>
          {!hasChecks ? (
            <div style={{ textAlign: 'center', padding: '18px 0', color: 'var(--g400)', fontSize: 12 }}>No checks selected yet.</div>
          ) : (
            <div>
              {checks.map(([name, price], i) => {
                const qty = qtys[i] ?? 0;
                if (!qty) return null;
                return (
                  <div key={name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--g100)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ fontSize: 10, background: 'var(--or)', color: 'var(--nv)', fontWeight: 800, borderRadius: 50, padding: '2px 7px', whiteSpace: 'nowrap' }}>{qty}x</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nv)' }}>{name}</span>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--or-dk)' }}>₹{(qty * price).toLocaleString('en-IN')}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="amtbox">
          <div className="amtrow">
            <span className="amtl">{isInvite ? 'Total on submission' : 'Total Payable Now'}</span>
            <span className="amtv">₹{total.toLocaleString('en-IN')}</span>
          </div>
        </div>

        <div className="hr" />

        <div className="flex jb aic mb8 ts"><span className="tg">Wallet balance</span><span className="f7 t-gr">₹{WALLET.toLocaleString('en-IN')}</span></div>
        <div className="flex jb aic mb8 ts">
          <span className="tg">{isInvite ? 'After candidate submits' : 'Balance after order'}</span>
          <span className="f7" style={{ color: insufficient ? 'var(--rd)' : 'var(--nv)' }}>₹{after.toLocaleString('en-IN')}</span>
        </div>

        {insufficient && (
          <div className="alert a-rd mt8">
            <div className="aico">🔴</div>
            <div className="abody"><div className="atitle">Insufficient credits</div>Your wallet balance is insufficient. Please top up your wallet before placing this order.</div>
          </div>
        )}
        {insufficient && (
          <button className="btn bpri wf mt12" onClick={() => onNavigate('wallet')}>+ Add Credits First →</button>
        )}
      </div>

      <div className="alert a-bl mb16">
        <div className="aico">ℹ️</div>
        <div className="abody" style={{ fontSize: '11.5px' }}>
          {isInvite
            ? 'By sending this invite you confirm written consent has been obtained from the candidate per applicable law.'
            : 'By placing this order you confirm written consent has been obtained from the candidate per applicable law.'}
        </div>
      </div>

      <button
        className="btn bsec wf blg"
        style={!hasChecks || insufficient || submitting ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
        disabled={!hasChecks || insufficient || submitting}
        onClick={hasChecks && !insufficient && !submitting ? onPlace : undefined}
      >
        {submitting
          ? <><span className="spinner" style={{ width: 14, height: 14 }} />{isInvite ? ' Sending…' : ' Placing…'}</>
          : hasChecks
            ? (isInvite ? `📤 Send Invite Link — ₹${total.toLocaleString('en-IN')}` : `⚡ Place Order — ₹${total.toLocaleString('en-IN')}`)
            : (isInvite ? '📤 Send Invite Link' : '⚡ Place Order — ₹0')}
      </button>
      <button className="btn bghost wf mt8" onClick={onCancel}>Cancel</button>
    </div>
  );
}

function InviteForm({ onNavigate, addToast, projectList, checks, otherChecks, clientId, onReset, onSuccess }) {
  const [form, setForm] = useState({ projectId: '', firstname: '', lastname: '', mobile: '', dob: '', email: '', empnumber: '', Remarks: '' });
  const [qtys, setQtys] = useState(() => new Array(checks.length).fill(0));
  const [packageId, setPackageId] = useState('');
  const [packagePrice, setPackagePrice] = useState(0);
  const [baseQtys, setBaseQtys] = useState(null);
  const [pkgLabel, setPkgLabel] = useState('');
  const [effectiveChecks, setEffectiveChecks] = useState(() => checks.map(([name]) => [name, 0]));
  const [packageList, setPackageList] = useState([]);
  const [packagesLoaded, setPackagesLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function clearError(k) { if (errors[k]) setErrors(e => ({ ...e, [k]: false })); }
  const errStyle = k => errors[k] ? { borderColor: 'var(--rd)', boxShadow: '0 0 0 2px rgba(229,62,62,.15)' } : {};

  useEffect(() => {
    if (!form.projectId) { setPackageList([]); setPackagesLoaded(false); return; }
    setPackagesLoaded(false);
    ordersApi.getPackagesList(form.projectId)
      .then(res => setPackageList(Array.isArray(res) ? res : []))
      .catch(() => setPackageList([]))
      .finally(() => setPackagesLoaded(true));
  }, [form.projectId]);

  useEffect(() => {
    if (projectList.length === 1) {
      setForm(f => f.projectId === projectList[0].value ? f : { ...f, projectId: projectList[0].value });
    }
  }, [projectList]);

  async function handleSubmit() {
    const errs = {};
    if (!form.projectId) errs.projectId = true;
    if (!form.firstname) errs.firstname = true;
    if (!form.lastname) errs.lastname = true;
    if (!form.mobile) errs.mobile = true;
    if (!form.dob) errs.dob = true;
    if (!form.email) errs.email = true;
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      addToast('Please fill required fields', 'error');
      const firstId = errs.projectId ? 'inv-project' : errs.firstname ? 'inv-firstname' : errs.lastname ? 'inv-lastname' : errs.mobile ? 'inv-mobile' : errs.dob ? 'inv-dob' : 'inv-email';
      document.getElementById(firstId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const { total } = calcTotals(qtys, effectiveChecks, packagePrice, baseQtys);
    if (!total) {
      addToast('Please select at least one check', 'error');
      document.getElementById('inv-scope')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('clientid', clientId || '');
      fd.append('projectid', form.projectId);
      fd.append('firstname', form.firstname);
      fd.append('lastname', form.lastname);
      fd.append('mobile', form.mobile);
      fd.append('dob', formatDisplay(form.dob));
      fd.append('email', form.email);
      fd.append('empnumber', form.empnumber);
      fd.append('packageid', packageId);
      fd.append('Remarks', form.Remarks);
      (otherChecks || []).forEach((c, i) => {
        fd.append(`otherChecks[${i}].CHECK_NAME`, c.check_name ?? c.CHECK_NAME ?? '');
        fd.append(`otherChecks[${i}].CHECK_ID`, c.check_id ?? c.CHECK_ID ?? '');
        fd.append(`otherChecks[${i}].IS_MASTER_TYPE`, c.is_master_type ?? c.IS_MASTER_TYPE ?? '');
        fd.append(`otherChecks[${i}].MAIN_CHECKTYPE`, c.main_checktype ?? c.MAIN_CHECKTYPE ?? '');
        fd.append(`otherChecks[${i}].Other_Count`, qtys[i] ?? 0);
      });
      const res = await ordersApi.orderCandidatePost(fd);
      addToast(res?.message || 'Invite sent successfully!', 'success');
      onSuccess?.();
      onReset();
    } catch (e) {
      addToast(e.message || 'Failed to send invite', 'error');
    } finally { setSubmitting(false); }
  }

  return (
    <div className="gr2" style={{ alignItems: 'start' }}>
      <div>
        <div className="card mb16">
          <div className="chd"><div className="ctitle">Candidate Details</div></div>
          <div className="fgrid">
            {projectList.length > 1 && (
              <div className="fg full">
                <label className="flbl">Project <span className="req">*</span></label>
                <select id="inv-project" className="sel" style={errStyle('projectId')} value={form.projectId} onChange={e => { set('projectId', e.target.value); clearError('projectId'); }}>
                  <option value="">— Select a project —</option>
                  {projectList.map(p => (
                    <option key={p.value} value={p.value} disabled={p.disabled}>{p.text}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="fg"><label className="flbl">First Name <span className="req">*</span></label><input id="inv-firstname" className="inp" style={errStyle('firstname')} placeholder="First Name" value={form.firstname} onChange={e => { set('firstname', e.target.value); clearError('firstname'); }} /></div>
            <div className="fg"><label className="flbl">Last Name <span className="req">*</span></label><input id="inv-lastname" className="inp" style={errStyle('lastname')} placeholder="Last Name" value={form.lastname} onChange={e => { set('lastname', e.target.value); clearError('lastname'); }} /></div>
            <div className="fg"><label className="flbl">Mobile No. <span className="req">*</span></label><input id="inv-mobile" className="inp" style={errStyle('mobile')} placeholder="+91 XXXXX XXXXX" value={form.mobile} onChange={e => { set('mobile', e.target.value); clearError('mobile'); }} /></div>
            <div className="fg"><label className="flbl">Date of Birth <span className="req">*</span></label><DateField id="inv-dob" block value={form.dob} onChange={v => { set('dob', v); clearError('dob'); }} inputStyle={errStyle('dob')} /></div>
            <div className="fg"><label className="flbl">Email Address <span className="req">*</span></label><input id="inv-email" className="inp" style={errStyle('email')} placeholder="candidate@company.com" value={form.email} onChange={e => { set('email', e.target.value); clearError('email'); }} /></div>
            <div className="fg"><label className="flbl">Employee / Ref ID</label><input className="inp" placeholder="Internal reference" value={form.empnumber} onChange={e => set('empnumber', e.target.value)} /></div>
            <div className="fg full"><label className="flbl">Remarks</label>
              <textarea className="ta" placeholder="e.g. Joining 1 June — expedite" value={form.Remarks} onChange={e => set('Remarks', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="card" id="inv-scope">
          <div className="chd"><div className="ctitle">Verification Scope</div></div>
          <VerificationScope
            key={`inv-vs-${form.projectId}`}
            checks={checks}
            qtys={qtys}
            onQtyChange={setQtys}
            onReset={() => setQtys(baseQtys ? [...baseQtys] : new Array(checks.length).fill(0))}
            packageList={packageList}
            packagesLoaded={packagesLoaded}
            onEffectiveChecksChange={setEffectiveChecks}
            onPackageChange={setPackageId}
            onPackagePriceChange={setPackagePrice}
            onBaseQtysChange={setBaseQtys}
            onPkgLabelChange={setPkgLabel}
          />
        </div>
      </div>

      <OrderSummary
        checks={effectiveChecks}
        qtys={qtys}
        isInvite={true}
        onPlace={handleSubmit}
        submitting={submitting}
        onCancel={() => onNavigate('orders')}
        onNavigate={onNavigate}
        packagePrice={packagePrice}
        baseQtys={baseQtys}
        pkgLabel={pkgLabel}
      />
    </div>
  );
}

const ALLOWED_DOC_EXTS = ['.xlsx', '.xls', '.doc', '.docx', '.pdf', '.zip', '.rar', '.png', '.jpg', '.jpeg'];
const MAX_DOC_SIZE = 100 * 1024 * 1024;

function DirectForm({ onNavigate, addToast, projectList, checks, otherChecks, clientId, onSuccess }) {
  const [form, setForm] = useState({ projectId: '', firstname: '', lastname: '', mobile: '', dob: '', email: '', empId: '', notes: '' });
  const [files, setFiles] = useState([]);
  const [fileDragOver, setFileDragOver] = useState(false);
  const [qtys, setQtys] = useState(() => new Array(checks.length).fill(0));
  const [packageId, setPackageId] = useState('');
  const [packagePrice, setPackagePrice] = useState(0);
  const [baseQtys, setBaseQtys] = useState(null);
  const [pkgLabel, setPkgLabel] = useState('');
  const [effectiveChecks, setEffectiveChecks] = useState(() => checks.map(([name]) => [name, 0]));
  const [packageList, setPackageList] = useState([]);
  const [packagesLoaded, setPackagesLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function clearError(k) { if (errors[k]) setErrors(e => ({ ...e, [k]: false })); }
  const errStyle = k => errors[k] ? { borderColor: 'var(--rd)', boxShadow: '0 0 0 2px rgba(229,62,62,.15)' } : {};

  useEffect(() => {
    if (!form.projectId) { setPackageList([]); setPackagesLoaded(false); return; }
    setPackagesLoaded(false);
    ordersApi.getPackagesList(form.projectId)
      .then(res => setPackageList(Array.isArray(res) ? res : []))
      .catch(() => setPackageList([]))
      .finally(() => setPackagesLoaded(true));
  }, [form.projectId]);

  useEffect(() => {
    if (projectList.length === 1) {
      setForm(f => f.projectId === projectList[0].value ? f : { ...f, projectId: projectList[0].value });
    }
  }, [projectList]);

  function addDocFiles(fileList) {
    const picked = Array.from(fileList);
    const valid = [];
    picked.forEach(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      if (!ALLOWED_DOC_EXTS.includes(ext)) { addToast(`${f.name}: File type not supported.`, 'error'); return; }
      if (f.size > MAX_DOC_SIZE) { addToast(`${f.name} exceeds the 100 MB size limit.`, 'error'); return; }
      valid.push(f);
    });
    setFiles(prev => {
      const existing = new Set(prev.map(f => f.name + f.size));
      return [...prev, ...valid.filter(f => !existing.has(f.name + f.size))];
    });
  }

  function handleFileChange(e) {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    addDocFiles(picked);
  }

  async function handleSubmit() {
    const errs = {};
    if (!form.projectId) errs.projectId = true;
    if (!form.firstname) errs.firstname = true;
    if (!form.lastname) errs.lastname = true;
    if (!form.mobile) errs.mobile = true;
    if (!form.dob) errs.dob = true;
    if (!form.email) errs.email = true;
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      addToast('Please fill required fields', 'error');
      const firstId = errs.projectId ? 'dir-project' : errs.firstname ? 'dir-firstname' : errs.lastname ? 'dir-lastname' : errs.mobile ? 'dir-mobile' : errs.dob ? 'dir-dob' : 'dir-email';
      document.getElementById(firstId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const { total } = calcTotals(qtys, effectiveChecks, packagePrice, baseQtys);
    if (!total) {
      addToast('Please select at least one check', 'error');
      document.getElementById('dir-scope')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('clientid', clientId || '');
      fd.append('projectid', form.projectId);
      fd.append('firstname', form.firstname);
      fd.append('lastname', form.lastname);
      fd.append('mobile', form.mobile);
      fd.append('dob', formatDisplay(form.dob));
      fd.append('email', form.email);
      fd.append('empnumber', form.empId);
      fd.append('packageid', packageId);
      fd.append('Remarks', form.notes);
      (otherChecks || []).forEach((c, i) => {
        fd.append(`otherChecks[${i}].CHECK_NAME`, c.check_name ?? c.CHECK_NAME ?? '');
        fd.append(`otherChecks[${i}].CHECK_ID`, c.check_id ?? c.CHECK_ID ?? '');
        fd.append(`otherChecks[${i}].IS_MASTER_TYPE`, c.is_master_type ?? c.IS_MASTER_TYPE ?? '');
        fd.append(`otherChecks[${i}].MAIN_CHECKTYPE`, c.main_checktype ?? c.MAIN_CHECKTYPE ?? '');
        fd.append(`otherChecks[${i}].Other_Count`, qtys[i] ?? 0);
      });
      files.forEach(file => fd.append('SingleUpload', file));
      const res = await ordersApi.orderReportPost(fd);
      addToast(res?.message || 'Order placed successfully!', 'success');
      onSuccess?.();
      onNavigate('orders');
    } catch (e) {
      addToast(e.message || 'Failed to place order', 'error');
    } finally { setSubmitting(false); }
  }

  return (
    <div className="gr2" style={{ alignItems: 'start' }}>
      <div>
        <div className="card mb16">
          <div className="chd"><div className="ctitle">Candidate Details</div></div>
          <div className="fgrid">
            {projectList.length > 1 && (
              <div className="fg full">
                <label className="flbl">Project <span className="req">*</span></label>
                <select id="dir-project" className="sel" style={errStyle('projectId')} value={form.projectId} onChange={e => { set('projectId', e.target.value); clearError('projectId'); }}>
                  <option value="">— Select a project —</option>
                  {projectList.map(p => (
                    <option key={p.value} value={p.value} disabled={p.disabled}>{p.text}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="fg"><label className="flbl">First Name <span className="req">*</span></label><input id="dir-firstname" className="inp" style={errStyle('firstname')} placeholder="First Name" value={form.firstname} onChange={e => { set('firstname', e.target.value); clearError('firstname'); }} /></div>
            <div className="fg"><label className="flbl">Last Name <span className="req">*</span></label><input id="dir-lastname" className="inp" style={errStyle('lastname')} placeholder="Last Name" value={form.lastname} onChange={e => { set('lastname', e.target.value); clearError('lastname'); }} /></div>
            <div className="fg"><label className="flbl">Mobile No. <span className="req">*</span></label><input id="dir-mobile" className="inp" style={errStyle('mobile')} placeholder="+91 XXXXX XXXXX" value={form.mobile} onChange={e => { set('mobile', e.target.value); clearError('mobile'); }} /></div>
            <div className="fg"><label className="flbl">Date of Birth <span className="req">*</span></label><DateField id="dir-dob" block value={form.dob} onChange={v => { set('dob', v); clearError('dob'); }} inputStyle={errStyle('dob')} /></div>
            <div className="fg"><label className="flbl">Email Address <span className="req">*</span></label><input id="dir-email" className="inp" style={errStyle('email')} placeholder="candidate@company.com" value={form.email} onChange={e => { set('email', e.target.value); clearError('email'); }} /></div>
            <div className="fg"><label className="flbl">Employee / Ref ID</label><input className="inp" placeholder="Internal reference" value={form.empId} onChange={e => set('empId', e.target.value)} /></div>
            <div className="fg full"><label className="flbl">Internal Notes</label>
              <textarea className="ta" placeholder="e.g. Urgent — joining 1 June" value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="card mb16">
          <div className="chd"><div className="ctitle">Upload Documents</div><div className="csub">Optional supporting documents</div></div>
          <label
            className="upzone"
            style={{ display: 'block', padding: '10px', ...(fileDragOver ? { borderColor: 'var(--or)', background: 'var(--or-lt)', boxShadow: '0 0 0 2px rgba(245,166,35,.2)' } : {}) }}
            onDragOver={e => { e.preventDefault(); setFileDragOver(true); }}
            onDragLeave={() => setFileDragOver(false)}
            onDrop={e => { e.preventDefault(); setFileDragOver(false); addDocFiles(e.dataTransfer.files); }}
          >
            <input type="file" multiple accept=".xlsx,.xls,.doc,.docx,.pdf,.zip,.rar,.png,.jpg,.jpeg" onChange={handleFileChange} style={{ display: 'none' }} />
            <div style={{ fontSize: 28, marginBottom: 8 }}>📂</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}><strong>Click to upload</strong> or drag &amp; drop documents</div>
            <div style={{ fontSize: 11, color: 'var(--g400)', marginTop: 6 }}>
              .xlsx &nbsp;·&nbsp; .xls &nbsp;·&nbsp; .doc &nbsp;·&nbsp; .docx &nbsp;·&nbsp; .pdf &nbsp;·&nbsp; .zip &nbsp;·&nbsp; .rar &nbsp;·&nbsp; .png &nbsp;·&nbsp; .jpg &nbsp;·&nbsp; .jpeg &nbsp;·&nbsp; Upto 100 MB accepted
            </div>
          </label>
          {files.map((f, i) => (
            <div key={i} className="upfile mt8">
              <div className="ufinfo"><span style={{ fontSize: 16 }}>📄</span><div><div className="ufname">{f.name}</div><div className="ufsize">{(f.size / 1024).toFixed(0)} KB</div></div></div>
              <span className="ufrm" onClick={() => setFiles(fs => fs.filter((_, j) => j !== i))}>✕</span>
            </div>
          ))}
        </div>

        <div className="card" id="dir-scope">
          <div className="chd"><div className="ctitle">Verification Scope</div></div>
          <VerificationScope
            key={`dir-vs-${form.projectId}`}
            checks={checks}
            qtys={qtys}
            onQtyChange={setQtys}
            onReset={() => setQtys(baseQtys ? [...baseQtys] : new Array(checks.length).fill(0))}
            packageList={packageList}
            packagesLoaded={packagesLoaded}
            onEffectiveChecksChange={setEffectiveChecks}
            onPackageChange={setPackageId}
            onPackagePriceChange={setPackagePrice}
            onBaseQtysChange={setBaseQtys}
            onPkgLabelChange={setPkgLabel}
          />
        </div>
      </div>

      <OrderSummary
        checks={effectiveChecks}
        qtys={qtys}
        isInvite={false}
        onPlace={handleSubmit}
        submitting={submitting}
        onCancel={() => onNavigate('orders')}
        onNavigate={onNavigate}
        packagePrice={packagePrice}
        baseQtys={baseQtys}
        pkgLabel={pkgLabel}
      />
    </div>
  );
}

export default function NewOrder({ onNavigate, initialTab = 'invite' }) {
  const [tab, setTab] = useState(initialTab);
  const { addToast, refreshWalletBalance, invitationExpireDay } = useApp();
  const [projectList, setProjectList] = useState([]);
  const [clientId, setClientId] = useState('');
  const [inviteKey, setInviteKey] = useState(0);
  const [walletBalance, setWalletBalance] = useState(0);
  const [alacarteChecks, setAlacarteChecks] = useState(null); // null = loading, array = loaded
  const [otherChecks, setOtherChecks] = useState(null);       // otherchecks from API

  function fetchWallet() {
    walletApi.getWalletDetails()
      .then(res => { const bal = Array.isArray(res) ? res[0]?.current_balance : res?.current_balance; if (bal != null) { WALLET = bal; setWalletBalance(bal); } })
      .catch(() => {});
  }

  function handleOrderSuccess() {
    fetchWallet();
    refreshWalletBalance();
  }

  useEffect(() => { fetchWallet(); }, []);

  useEffect(() => {
    ordersApi.orderCandidate()
      .then(res => {
        if (res?.clientid) setClientId(res.clientid);
        if (res?.projectlist) setProjectList(res.projectlist);
        if (res?.alacartejson) {
          try { setAlacarteChecks(JSON.parse(res.alacartejson)); }
          catch { setAlacarteChecks([]); }
        } else {
          setAlacarteChecks([]);
        }
        if (res?.otherchecks) setOtherChecks(res.otherchecks);
        else setOtherChecks([]);
      })
      .catch(() => { setAlacarteChecks([]); setOtherChecks([]); });
  }, []);

  // Build price lookup from alacartejson, then derive [name, price] tuples.
  // otherchecks is the primary list; alacartejson is the price reference.
  const checks = useMemo(() => {
    const priceMap = {};
    (alacarteChecks || []).forEach(c => {
      if (c.Text) priceMap[c.Text.toUpperCase()] = parseInt(c.Price, 10) || 0;
    });
    if (otherChecks && otherChecks.length > 0) {
      return otherChecks.map(c => [c.check_name, priceMap[c.check_name?.toUpperCase()] || 0]);
    }
    if (alacarteChecks && alacarteChecks.length > 0) {
      return alacarteChecks.map(c => [c.Text, parseInt(c.Price, 10) || 0]);
    }
    return CHECKS_FALLBACK;
  }, [otherChecks, alacarteChecks]);

  // remount forms when API checks arrive so qtys initialise with correct length
  const checksKey = (otherChecks !== null || alacarteChecks !== null) ? 'loaded' : 'loading';

  return (
    <>
      <TopNav currentPage="new-order" onNavigate={onNavigate} />
      <div className="pg">
        <div className="phd">
          <div>
            <div className="ptitle">New BGV Order</div>
            <div className="psub">
              {tab === 'invite' ? 'Initiate a background verification for a candidate'
                : tab === 'direct' ? 'Upload documents directly on behalf of the candidate'
                : 'Upload multiple candidates at once via CSV / Excel template'}
            </div>
          </div>
          <button className="btn bghost" onClick={() => onNavigate('orders')}>← Back to My Orders</button>
        </div>

        <div className="otabs" style={{ maxWidth: 480 }}>
          <div className={`otab${tab === 'invite' ? ' on' : ''}`} onClick={() => setTab('invite')}>📤 Invite Candidate</div>
          <div className={`otab${tab === 'direct' ? ' on' : ''}`} onClick={() => setTab('direct')}>⚡ Direct Order</div>
          <div className={`otab${tab === 'bulk' ? ' on' : ''}`} onClick={() => onNavigate('bulk-upload')}>📊 Bulk Upload</div>
        </div>

        {tab === 'invite' && (
          <div className="alert a-bl mb16">
            <div className="aico">ℹ️</div>
            <div className="abody">
              <div className="atitle">How Invite works</div>
              A secure link is sent to the candidate via email. Credits are deducted only after they submit. {invitationExpireDay > 0 ? 'Auto-refund applies if no submission within ' + invitationExpireDay + ' days.' : ''}
            </div>
          </div>
        )}
        {tab === 'direct' && (
          <div className="alert a-or mb16">
            <div className="aico">⚡</div>
            <div className="abody">
              <div className="atitle">Immediate credit deduction</div>
              Credits are deducted as soon as this order is placed. Ensure written consent has been obtained from the candidate.
            </div>
          </div>
        )}

        {tab === 'invite' && (
          <InviteForm
            key={`invite-${checksKey}-${inviteKey}`}
            onNavigate={onNavigate}
            addToast={addToast}
            projectList={projectList}
            checks={checks}
            otherChecks={otherChecks || []}
            clientId={clientId}
            onReset={() => setInviteKey(k => k + 1)}
            onSuccess={handleOrderSuccess}
          />
        )}
        {tab === 'direct' && (
          <DirectForm
            key={`direct-${checksKey}`}
            onNavigate={onNavigate}
            addToast={addToast}
            projectList={projectList}
            checks={checks}
            otherChecks={otherChecks || []}
            clientId={clientId}
            onSuccess={handleOrderSuccess}
          />
        )}
      </div>
    </>
  );
}
